/**
 * SectionUnavailable — error fallback for the fee, certificate and myData
 * section views.
 *
 * Rendered by a PortErrorBoundary when the section's port slot is absent OR its
 * read rejects — including routine async outcomes (an FR-008 certificate block,
 * a server error, a request timeout) that replace the loading skeleton. Mirrors
 * ProfileUnavailable (the memberProfile fallback). Takes the unavailable message
 * (announced via role=alert) and the section's optional `title`, rendered as its
 * heading because the real heading lives inside the boundary that failed.
 *
 * The back affordance is owned by the route (item-4 r1 C5): `Profile` renders a
 * single "Takaisin" control above the heading for any member sub-view, OUTSIDE
 * the per-section error boundary, so it keeps working when a section degrades to
 * this fallback — the member is never stranded.
 *
 * No raw colour literals (FR-013) — token classes only.
 *
 * Owned by feature 026-proto-convergence-v2.
 * Task: T030
 */

import type { JSX } from 'react';

import { ProfileSectionHeader } from './ProfileSectionHeader.js';

export interface SectionUnavailableProps {
  readonly message: string;
  /**
   * The failed section's own heading. The section renders its heading INSIDE
   * the boundary that failed, so without this the degraded view has no heading
   * for heading navigation.
   */
  readonly title?: string;
}

export function SectionUnavailable({ message, title }: SectionUnavailableProps): JSX.Element {
  return (
    <div className="flex flex-col gap-4">
      {title === undefined ? null : <ProfileSectionHeader title={title} />}
      {/* role=alert: this is now a routine ASYNC outcome (an FR-008 block, a
          server error, the 30 s timeout) replacing the loading skeleton, so it
          must be announced — a silent swap tells a screen-reader user nothing. */}
      <p role="alert" className="text-sm text-muted-foreground">
        {message}
      </p>
    </div>
  );
}
